import axios from 'axios';
import { getActivePinia } from 'pinia';
import i18n from '@/i18n';
import { useNotificationStore } from '@/stores/notification';
import { useAuthStore } from '@/stores/auth';
import {
  applySessionTokens,
  clearAdminSessionStorage,
  refreshAdminSession,
} from '@/utils/authSession';
import { Establishment, EstablishmentRequest, LocationRequest } from '@/types/establishments';
import { MenuCategory, MenuCategoryRequest, MenuItemRequest, MenuItem } from '@/types/menu';
import { News, NewsRequest } from '@/types/news';
import type { Reward } from '@/types/rewards';
import { secureStorage } from '../utils/secureStorage';
import { CreateRewardRequest, UpdateRewardRequest, RedeemRewardRequest } from '@/types/rewards';
import { TableRequest, TableBatchRequest, QRBatchGenerateRequest } from '@/types/tables';
import type { AdminUser } from '@/types/admin';
import type { AnalyticsResponse, AnalyticsParams } from '@/types/analytics';
import type { OrderListItem } from '@/types/orders';
import type { MediaUploadScope } from '@/types/mediaUpload';
import { pickLocalizedDisplay } from '@/types/locale';
import { unwrapApiResponse, type ListMeta } from '@/utils/apiResponse';
import { runInFlight } from '@/utils/inFlightByKey';
import { evaluateLicenseStatus } from '@/composables/useLicenseStatus';
import type { ProjectSummary } from '@/stores/projectContext';
import type {
  StripeConnectAccountCreated,
  StripeConnectAccountLink,
  StripeConnectAccountSession,
  StripeConnectStatus,
} from '@/constants/stripeConnect';

export interface ProjectStripeSettings {
  connected: boolean;
  livemode: boolean;
  publishable_key?: string;
  restricted_key_set: boolean;
  payment_webhook_secret_set: boolean;
  connect_webhook_secret_set: boolean;
  connected_at?: string;
  payment_webhook_url: string;
  connect_webhook_url: string;
}

export interface ProjectPaymentSettings {
  provider_code: string;
  stripe: ProjectStripeSettings;
}

export interface EstablishmentPaymentContext {
  provider_code: string;
  stripe_connected: boolean;
}

export interface StripeProjectConnectRequest {
  livemode: boolean;
  publishable_key: string;
  restricted_key: string;
  payment_webhook_secret?: string;
  connect_webhook_secret?: string;
}

const rawApiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : rawApiUrl.replace(/\/?$/, '') + '/api';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

type RefreshResponse = {
  token: string;
  refresh_token: string;
};

export function extractApiList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload != null && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    return Array.isArray(data) ? (data as T[]) : [];
  }
  if (payload != null && typeof payload === 'object' && Array.isArray((payload as { items?: unknown }).items)) {
    return (payload as { items: T[] }).items;
  }
  return [];
}

apiClient.interceptors.request.use(
  (config) => {
    const token = secureStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeProjectId = secureStorage.getActiveProjectId();
    if (activeProjectId) {
      config.headers['X-Project-Id'] = activeProjectId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const contentType = response.headers?.['content-type'] ?? '';
    if (
      response.data != null &&
      typeof response.data === 'object' &&
      !(response.data instanceof Blob) &&
      !String(contentType).includes('application/pdf')
    ) {
      response.data = unwrapApiResponse(response.data);
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    if (status === 429) {
      const friendlyError = {
        ...error,
        message: 'Previše pokušaja. Pokušajte ponovno za minutu.',
      };
      return Promise.reject(friendlyError);
    }

    if (status === 403) {
      const data = error?.response?.data as { error?: string; message?: string; code?: string; error_code?: string } | undefined;
      const coded = data?.error_code ?? data?.code;
      if (coded?.startsWith('LICENSE_')) {
        const pinia = getActivePinia();
        if (pinia) {
          const auth = useAuthStore(pinia);
          const alreadyBlocked =
            auth.user?.role === 'admin' &&
            !evaluateLicenseStatus(auth.user.license as import('@/types/admin').License | undefined).isValid;
          if (!alreadyBlocked) {
            const key = `license.errors.${coded}`;
            const translated = i18n.global.t(key);
            const message =
              translated !== key ? translated : (data?.message ?? data?.error ?? '');
            if (message) {
              useNotificationStore(pinia).warning(String(message));
            }
          }
        }
      } else if (!coded) {
        const errMsg = data?.message ?? data?.error;
        if (typeof errMsg === 'string' && errMsg.toLowerCase().includes('viewer')) {
          const pinia = getActivePinia();
          if (pinia) {
            useNotificationStore(pinia).warning(i18n.global.t('common.viewerReadOnlyError'));
          }
        }
      }
    }

    const originalRequest = error?.config;
    const isUnauthorized = status === 401;
    const isAlreadyRetried = !!originalRequest?._retry;
    const shouldSkipRefresh = !!originalRequest?.headers?.['x-skip-auth-refresh'];
    const requestUrl: string = originalRequest?.url ?? '';
    const isAuthRoute =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/admin/auth/login') ||
      requestUrl.includes('/admin/auth/refresh') ||
      requestUrl.includes('/admin/auth/logout');

    if (isUnauthorized && !isAlreadyRetried && !shouldSkipRefresh && !isAuthRoute) {
      const isAdmin = secureStorage.getIsAdmin();
      if (!isAdmin) {
        clearAdminSessionStorage();
        return Promise.reject(error);
      }

      const refreshToken = secureStorage.getRefreshToken();
      if (!refreshToken) {
        clearAdminSessionStorage();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const refreshed = await refreshAdminSession();
        if (!refreshed) {
          clearAdminSessionStorage();
          return Promise.reject(error);
        }

        applySessionTokens(refreshed.token, refreshed.refresh_token);

        const pinia = getActivePinia();
        if (pinia) {
          useAuthStore(pinia).applyTokensFromRefresh(
            refreshed.token,
            refreshed.refresh_token,
            refreshed.admin ?? null,
          );
        }

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        clearAdminSessionStorage();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export type { MediaUploadScope } from '@/types/mediaUpload';

const postMediaUpload = async (
  file: File,
  scope?: MediaUploadScope
): Promise<{ uuid: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (scope?.establishmentId) {
    formData.append('establishment_uuid', scope.establishmentId);
  }
  if (scope?.projectId) {
    formData.append('project_uuid', scope.projectId);
  }
  let url = '/media/upload';
  if (scope?.context === 'profile') {
    const q = new URLSearchParams({ context: 'profile' });
    url = `/media/upload?${q.toString()}`;
  }

  const response = await apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export interface MediaApiClient {
  uploadFile: (file: File, scope?: MediaUploadScope) => Promise<{ uuid: string }>;
  getFileUrl: (uuid: string) => string | null;
}

export const mediaApi: MediaApiClient = {
  uploadFile: postMediaUpload,

  getFileUrl: (uuid: string) => {
    if (!uuid) return null;
    return `${API_URL}/media/file/${uuid}`;
  }
};

export type { ListMeta } from '@/utils/apiResponse';

export interface EstablishmentListResponse {
  data: Establishment[];
  meta: ListMeta;
}

export interface EstablishmentDeletePreview {
  location_count: number;
  details_count: number;
  working_hours_count: number;
  office_admin_count: number;
  office_admin_refresh_token_count: number;
  office_admin_device_token_count: number;
  promo_code_count: number;
  user_establishment_count: number;
  admin_establishment_count: number;
  complex_link_count: number;
  transaction_count: number;
  card_points_count: number;
  qr_code_count: number;
  qr_transaction_count: number;
  order_count: number;
  order_item_count: number;
  table_count: number;
  reward_count: number;
  user_reward_count: number;
  news_count: number;
  news_image_count: number;
  menu_category_count: number;
  menu_item_count: number;
}

export const establishmentApi = {

  getAll: async (
    params?: {
      active_only?: boolean;
      page?: number;
      page_size?: number;
      search?: string;
      type?: string;
      filter_assigned_admin_uuid?: string;
      filter_project_owner_uuid?: string;
    }
  ): Promise<Establishment[] | EstablishmentListResponse> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.active_only !== undefined) searchParams.set('active_only', String(params.active_only));
      if (params?.page != null) searchParams.set('page', String(params.page));
      if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
      if (params?.search) searchParams.set('search', params.search);
      if (params?.type && params.type !== 'all') searchParams.set('type', params.type);
      if (params?.filter_assigned_admin_uuid)
        searchParams.set('filter_assigned_admin_uuid', params.filter_assigned_admin_uuid);
      if (params?.filter_project_owner_uuid)
        searchParams.set('filter_project_owner_uuid', params.filter_project_owner_uuid);
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments${query ? `?${query}` : ''}`);
      return response.data as Establishment[] | EstablishmentListResponse;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id: string) => {
    return runInFlight(`establishment:${id}`, async () => {
      const response = await apiClient.get(`/establishments/${id}`);
      return response.data;
    });
  },

  getQr: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/establishments/${id}/qr`, { responseType: 'blob' });
    return response.data as Blob;
  },

  create: async (establishment: EstablishmentRequest) => {
    try {
      const payload: Record<string, unknown> = {
        name_i18n: establishment.name_i18n,
        description_i18n: establishment.description_i18n,
        type: establishment.type,
        website: establishment.website || undefined,
        menu_scope: establishment.menu_scope,
      };

      if (establishment.logo_uuid && establishment.logo_uuid !== '') {
        payload.logo_uuid = establishment.logo_uuid;
      }
      if (establishment.logo_background_color != null && establishment.logo_background_color.trim() !== '') {
        payload.logo_background_color = establishment.logo_background_color.trim();
      }
      if (establishment.hero_image_uuid && establishment.hero_image_uuid !== '') {
        payload.hero_image_uuid = establishment.hero_image_uuid;
      }

      const response = await apiClient.post('/establishments', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string, establishment: EstablishmentRequest) => {
    try {
      const payload: Record<string, unknown> = {
        name_i18n: establishment.name_i18n,
        description_i18n: establishment.description_i18n,
        type: establishment.type,
        website: establishment.website || undefined,
        points_per_euro_scan: establishment.points_per_euro_scan,
        points_per_euro_order: establishment.points_per_euro_order,
        menu_scope: establishment.menu_scope,
      };

      if (establishment.logo_background_color != null && establishment.logo_background_color.trim() !== '') {
        payload.logo_background_color = establishment.logo_background_color.trim();
      } else {
        payload.logo_background_color = '';
      }

      if (establishment.logo_uuid === '' || establishment.logo_uuid === undefined) {
        Object.assign(payload, { logo_uuid: null });
      } else if (establishment.logo_uuid) {
        Object.assign(payload, { logo_uuid: establishment.logo_uuid });
      }

      if (establishment.hero_image_uuid === '' || establishment.hero_image_uuid === undefined) {
        Object.assign(payload, { hero_image_uuid: null });
      } else if (establishment.hero_image_uuid) {
        Object.assign(payload, { hero_image_uuid: establishment.hero_image_uuid });
      }

      const response = await apiClient.put(`/establishments/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setStatus: async (id: string, active: boolean) => {
    const response = await apiClient.patch(`/establishments/${id}/status?active=${active}`);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/establishments/${id}`);
    return response.data;
  },

  getDeletePreview: async (id: string): Promise<EstablishmentDeletePreview> => {
    const response = await apiClient.get(`/establishments/${id}/delete-preview`);
    return response.data;
  },

  getAnalytics: async (establishmentId: string, params?: AnalyticsParams): Promise<AnalyticsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    const query = searchParams.toString();
    const url = `/establishments/${establishmentId}/analytics${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }
};

export interface ComplexEstablishmentRef {
  uuid: string;
  name: string;
  type: string;
}

export interface Complex {
  uuid: string;
  slug: string;
  name: string;
  description: string;
  logo_uuid?: string;
  logo?: string;
  points_scope: 'establishment' | 'complex';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  establishments?: ComplexEstablishmentRef[];
}

export interface ComplexRequest {
  slug?: string;
  name: string;
  description?: string;
  logo_uuid?: string;
  points_scope?: 'establishment' | 'complex';
  is_active?: boolean;
  establishment_uuids?: string[];
}

export const complexesApi = {
  getAll: async (): Promise<Complex[]> => {
    const response = await apiClient.get('/admin/complexes');
    return response.data;
  },
  getById: async (id: string): Promise<Complex> => {
    const response = await apiClient.get(`/admin/complexes/${id}`);
    return response.data;
  },
  create: async (payload: ComplexRequest): Promise<Complex> => {
    const response = await apiClient.post('/admin/complexes', payload);
    return response.data;
  },
  update: async (id: string, payload: ComplexRequest): Promise<Complex> => {
    const response = await apiClient.put(`/admin/complexes/${id}`, payload);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/complexes/${id}`);
  }
};

export const locationApi = {

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/locations/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAnalytics: async (locationId: string, params?: AnalyticsParams): Promise<AnalyticsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    const query = searchParams.toString();
    const url = `/locations/${locationId}/analytics${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getAll: async (activeOnly = false) => {
    try {
      const response = await apiClient.get('/locations');
      let locations = Array.isArray(response.data) ? response.data : [];

      if (activeOnly) {
        locations = locations.filter((loc: { is_active?: boolean }) => loc.is_active);
      }

      return { locations };
    } catch (error) {

      throw error;
    }
  },

  getLocationCountByEstablishmentId: async (establishmentId: string) => {
    try {
      const response = await apiClient.get(`/establishments/${establishmentId}`);
      return response.data.locations ? response.data.locations.length : 0;
    } catch (error) {
      return 0;
    }
  },

  create: async (establishmentId: string, location: LocationRequest) => {
    try {
      const response = await apiClient.post(`/establishments/${establishmentId}/locations`, location);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string, location: LocationRequest) => {
    const response = await apiClient.put(`/locations/${id}`, location);
    return response.data;
  },

  setStatus: async (id: string, active: boolean) => {
    const response = await apiClient.patch(`/locations/${id}/status?active=${active}`);
    return response.data;
  }
};

export interface MenuCategoryListResponse {
  data: MenuCategory[];
  meta: ListMeta;
}

export interface MenuItemListResponse {
  data: MenuItem[];
  meta: ListMeta;
}

function buildMenuItemPayload(
  item: MenuItemRequest,
  options?: { imageUuid?: string; includeNullSpecialPrice?: boolean },
): Record<string, unknown> {
  const nameLegacy = pickLocalizedDisplay(item.name_i18n, item.name);
  const descLegacy = pickLocalizedDisplay(item.description_i18n, item.description);

  const payload: Record<string, unknown> = {
    category_id: item.category_id,
    name: nameLegacy,
    description: descLegacy,
    price: item.price,
    order_number: item.order_number || 0,
    is_out_of_stock: item.is_out_of_stock ?? false,
  };

  if (item.name_i18n) {
    payload.name_i18n = { hr: item.name_i18n.hr ?? '', en: item.name_i18n.en ?? '' };
  }
  if (item.description_i18n) {
    payload.description_i18n = {
      hr: item.description_i18n.hr ?? '',
      en: item.description_i18n.en ?? '',
    };
  }

  if (options?.includeNullSpecialPrice) {
    payload.special_price = item.special_price ?? null;
    payload.special_price_valid_from = item.special_price_valid_from || null;
    payload.special_price_valid_to = item.special_price_valid_to || null;
  } else {
    if (item.special_price != null) payload.special_price = item.special_price;
    if (item.special_price_valid_from) payload.special_price_valid_from = item.special_price_valid_from;
    if (item.special_price_valid_to) payload.special_price_valid_to = item.special_price_valid_to;
  }

  const imageUuid = options?.imageUuid ?? item.image_uuid;
  if (imageUuid) payload.image_uuid = imageUuid;

  if (item.fields != null) {
    payload.fields = item.fields;
  }
  if (item.pos_article_id !== undefined) payload.pos_article_id = item.pos_article_id;
  if (item.pos_article_code !== undefined) payload.pos_article_code = item.pos_article_code;

  return payload;
}

export const menuApi = {
  getCategories: async (
    establishmentId: string,
    params?: { page?: number; page_size?: number; include_items?: boolean; location_uuid?: string }
  ): Promise<MenuCategory[] | MenuCategoryListResponse> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page != null) searchParams.set('page', String(params.page));
      if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
      if (params?.include_items) searchParams.set('include_items', 'true');
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments/${establishmentId}/menu/categories${query ? `?${query}` : ''}`);
      return response.data as MenuCategory[] | MenuCategoryListResponse;
    } catch (error) {
      throw error;
    }
  },

  getCategoryById: async (establishmentId: string, categoryId: string, params?: { location_uuid?: string }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments/${establishmentId}/menu/categories/${categoryId}${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createCategory: async (
    establishmentId: string,
    category: MenuCategoryRequest,
    params?: { location_uuid?: string }
  ) => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.post(`/establishments/${establishmentId}/menu/categories${query ? `?${query}` : ''}`, category);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateCategory: async (
    establishmentId: string,
    categoryId: string,
    category: MenuCategoryRequest,
    params?: { location_uuid?: string }
  ) => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.put(`/establishments/${establishmentId}/menu/categories/${categoryId}${query ? `?${query}` : ''}`, category);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCategory: async (
    establishmentId: string,
    categoryId: string,
    params?: { location_uuid?: string }
  ) => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.delete(`/establishments/${establishmentId}/menu/categories/${categoryId}${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getItems: async (
    establishmentId: string,
    categoryId: string,
    params?: { page?: number; page_size?: number; location_uuid?: string }
  ): Promise<MenuItem[] | MenuItemListResponse> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page != null) searchParams.set('page', String(params.page));
      if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
      if (params?.location_uuid) searchParams.set('location_uuid', params.location_uuid);
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments/${establishmentId}/menu/categories/${categoryId}/items${query ? `?${query}` : ''}`);
      return response.data as MenuItem[] | MenuItemListResponse;
    } catch (error) {
      throw error;
    }
  },

  getItem: async (_establishmentId: string, itemId: string, params?: { location_uuid?: string }) => {
    try {
      const query = params?.location_uuid
        ? `?location_uuid=${encodeURIComponent(params.location_uuid)}`
        : '';
      const response = await apiClient.get<MenuItem>(`/admin/menu-items/${itemId}${query}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createItem: async (
    establishmentId: string,
    item: MenuItemRequest,
    imageFile?: File,
    params?: { location_uuid?: string },
  ) => {
    try {

      let imageUuid;
      if (imageFile) {
        const mediaResponse = await mediaApi.uploadFile(imageFile, { establishmentId: establishmentId });
        imageUuid = mediaResponse.uuid;
      }

      const payload = buildMenuItemPayload(item, { imageUuid });
      if (item.fields != null && item.fields.length === 0) {
        delete payload.fields;
      }

      const query = params?.location_uuid ? `?location_uuid=${encodeURIComponent(params.location_uuid)}` : '';
      const response = await apiClient.post(`/establishments/${establishmentId}/menu/items${query}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateItem: async (
    establishmentId: string,
    itemId: string,
    item: MenuItemRequest,
    imageFile?: File,
    params?: { location_uuid?: string },
  ) => {
    try {

      let imageUuid = item.image_uuid;
      if (imageFile) {
        const mediaResponse = await mediaApi.uploadFile(imageFile, { establishmentId: establishmentId });
        imageUuid = mediaResponse.uuid;
      }

      const payload = buildMenuItemPayload(item, {
        imageUuid,
        includeNullSpecialPrice: true,
      });

      const query = params?.location_uuid ? `?location_uuid=${encodeURIComponent(params.location_uuid)}` : '';
      const response = await apiClient.put(`/establishments/${establishmentId}/menu/items/${itemId}${query}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updateLocationItemOverride: async (
    locationId: string,
    itemId: string,
    override: {
      is_active?: boolean;
      is_out_of_stock?: boolean;
      price?: number;
      special_price?: number | null;
      special_price_valid_from?: string | null;
      special_price_valid_to?: string | null;
      order_number?: number;
    }
  ) => {
    const response = await apiClient.put(`/locations/${locationId}/menu/items/${itemId}/override`, override);
    return response.data;
  },
  deleteLocationItemOverride: async (locationId: string, itemId: string) => {
    const response = await apiClient.delete(`/locations/${locationId}/menu/items/${itemId}/override`);
    return response.data;
  },
  updateLocationCategoryOverride: async (
    locationId: string,
    categoryId: string,
    override: {
      is_active?: boolean;
      order_number?: number;
    }
  ) => {
    const response = await apiClient.put(`/locations/${locationId}/menu/categories/${categoryId}/override`, override);
    return response.data;
  },
  deleteLocationCategoryOverride: async (locationId: string, categoryId: string) => {
    const response = await apiClient.delete(`/locations/${locationId}/menu/categories/${categoryId}/override`);
    return response.data;
  },

  removeItemImage: async (establishmentId: string, itemId: string) => {
    try {
      const response = await apiClient.delete(`/establishments/${establishmentId}/menu/items/${itemId}/image`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteItem: async (establishmentId: string, itemId: string) => {
    try {
      const response = await apiClient.delete(`/establishments/${establishmentId}/menu/items/${itemId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getItemQr: async (establishmentId: string, itemId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/establishments/${establishmentId}/menu/items/${itemId}/qr`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  }
};

export interface NewsListResponse {
  data: News[];
  meta: ListMeta;
}

export interface NewsListParams {
  page?: number;
  page_size?: number;
  establishment_uuid?: string;
  search?: string;
  filter_assigned_admin_uuid?: string;
  filter_project_owner_uuid?: string;
}

export const newsApi = {
  listAll: async (params?: NewsListParams): Promise<News[] | NewsListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
    if (params?.establishment_uuid) searchParams.set('establishment_uuid', params.establishment_uuid);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.filter_assigned_admin_uuid) {
      searchParams.set('filter_assigned_admin_uuid', params.filter_assigned_admin_uuid);
    }
    if (params?.filter_project_owner_uuid) {
      searchParams.set('filter_project_owner_uuid', params.filter_project_owner_uuid);
    }
    const query = searchParams.toString();
    const response = await apiClient.get(`/admin/news${query ? `?${query}` : ''}`);
    return response.data as News[] | NewsListResponse;
  },

  getAll: async (establishmentId: string, params?: { page?: number; page_size?: number }): Promise<News[] | NewsListResponse> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page != null) searchParams.set('page', String(params.page));
      if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments/${establishmentId}/news${query ? `?${query}` : ''}`);
      return response.data as News[] | NewsListResponse;
    } catch (error) {
      throw error;
    }
  },

  getById: async (establishmentId: string, newsId: string) => {
    try {
      const response = await apiClient.get(`/establishments/${establishmentId}/news/${newsId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (establishmentId: string, news: NewsRequest) => {
    try {
      const response = await apiClient.post(`/establishments/${establishmentId}/news`, news);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  update: async (establishmentId: string, newsId: string, news: NewsRequest) => {
    try {
      const response = await apiClient.put(`/establishments/${establishmentId}/news/${newsId}`, news);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  delete: async (establishmentId: string, newsId: string) => {
    try {
      const response = await apiClient.delete(`/establishments/${establishmentId}/news/${newsId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const authApi = {
  login: async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  adminLogin: async (email: string, password: string) => {
    try {
      const projectUUID = secureStorage.getActiveProjectId()
      const response = await apiClient.post('/admin/auth/login', { email, password, project_uuid: projectUUID ?? undefined });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  adminRefresh: async (refreshToken: string) => {
    const response = await apiClient.post('/admin/auth/refresh', { refresh_token: refreshToken }, { headers: { 'x-skip-auth-refresh': '1' } });
    return response.data;
  },

  adminLogout: async (refreshToken: string) => {
    const response = await apiClient.post('/admin/auth/logout', { refresh_token: refreshToken }, { headers: { 'x-skip-auth-refresh': '1' } });
    return response.data;
  },

  registerAdmin: async (adminData: any) => {
    try {
      const response = await apiClient.post('/admin/auth/register', adminData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAdminProfile: async () => {
    try {
      const response = await apiClient.get('/admin/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateAdminAvatar: async (avatarUuid: string) => {
    const response = await apiClient.put('/admin/profile/avatar', { avatar_uuid: avatarUuid });
    return response.data;
  },

  deleteAdminAvatar: async () => {
    const response = await apiClient.delete('/admin/profile/avatar');
    return response.data;
  },

  adminForgotPassword: async (email: string) => {
    const response = await apiClient.post('/admin/auth/forgot-password', { email });
    return response.data;
  },

  adminResetPassword: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/admin/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  },

  acceptOfficeAdminInvite: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/auth/office-admin/accept-invite', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  acceptAdminInvite: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/admin/auth/accept-invite', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  changePassword: async (oldPassword: string, newPassword: string) => {
    const response = await apiClient.post('/admin/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    });
    return response.data;
  }
};

export const adminApi = {
  getPendingAdmins: async () => {
    try {
      const response = await apiClient.get('/admin/superadmin/pending-admins');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAllAdmins: async () => {
    try {
      const response = await apiClient.get('/admin/superadmin/admins');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getProjects: async (): Promise<ProjectSummary[]> => {
    const response = await apiClient.get('/admin/superadmin/projects')
    return extractApiList<ProjectSummary>(response.data)
  },

  getMyProjects: async (): Promise<ProjectSummary[]> => {
    const response = await apiClient.get('/admin/my-projects')
    return extractApiList<ProjectSummary>(response.data)
  },

  createProject: async (payload: { name: string }) => {
    const response = await apiClient.post('/admin/superadmin/projects', payload)
    return response.data
  },

  updateProject: async (
    projectUuid: string,
    payload: {
      name?: string
      points_mode?: string
      points_scope?: string
      loyalty_levels?: { name: string; min_points: number }[]
      referral_enabled?: boolean
      referral_referrer_points?: number
      referral_referee_points?: number
    },
  ) => {
    const response = await apiClient.put(`/admin/projects/${projectUuid}`, payload)
    return response.data
  },

  deleteProject: async (projectUuid: string) => {
    const response = await apiClient.delete(`/admin/superadmin/projects/${projectUuid}`)
    return response.data
  },

  getAllProjectAdmins: async (): Promise<Record<string, AdminUser[]>> => {
    const response = await apiClient.get('/admin/superadmin/projects/admins')
    const data = response.data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, AdminUser[]>
    }
    return {}
  },

  getProjectAdmins: async (projectUuid: string) => {
    const response = await apiClient.get(`/admin/superadmin/projects/${projectUuid}/admins`)
    return response.data
  },

  assignAdminToProject: async (projectUuid: string, adminUuid: string, projectMembershipRole: string) => {
    const response = await apiClient.post(`/admin/superadmin/projects/${projectUuid}/admins`, {
      admin_uuid: adminUuid,
      project_membership_role: projectMembershipRole
    })
    return response.data
  },

  transferProjectOwnership: async (projectUuid: string, newOwnerAdminUuid: string) => {
    const response = await apiClient.post(`/admin/superadmin/projects/${projectUuid}/transfer-ownership`, {
      new_owner_admin_uuid: newOwnerAdminUuid
    })
    return response.data
  },

  removeAdminFromProject: async (projectUuid: string, adminUuid: string) => {
    const response = await apiClient.delete(`/admin/superadmin/projects/${projectUuid}/admins/${adminUuid}`)
    return response.data
  },

  getAdminDetails: async (uuid: string) => {
    try {
      const response = await apiClient.get(`/admin/superadmin/admins/${uuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  approveAdmin: async (uuid: string, licenseData: {
    license_type: string,
    expiry_date: string,
    max_establishments: number,
    license_note?: string,
    project_uuid?: string,
    establishment_uuids?: string[],
    project_membership_role: string
  }) => {
    try {
      const response = await apiClient.post(`/admin/superadmin/admins/${uuid}/approve`, licenseData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  inviteAdmin: async (payload: {
    email: string
    first_name: string
    last_name: string
    license_type: string
    expiry_date: string
    max_establishments: number
    license_note?: string
    project_uuid: string
    establishment_uuids?: string[]
    project_membership_role: string
  }) => {
    const response = await apiClient.post('/admin/superadmin/admins/invite', payload)
    return response.data
  },

  resendAdminInvite: async (uuid: string) => {
    const response = await apiClient.post(`/admin/superadmin/admins/${uuid}/resend-invite`)
    return response.data
  },

  revokeAdminInvite: async (uuid: string) => {
    const response = await apiClient.post(`/admin/superadmin/admins/${uuid}/revoke-invite`)
    return response.data
  },

  updateLicense: async (uuid: string, data: {
    type?: string;
    status?: string;
    expiry_date?: string;
    max_establishments?: number;
    note?: string;
  }) => {
    try {
      const response = await apiClient.put(`/admin/superadmin/licenses/${uuid}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  rejectAdmin: async (uuid: string, reason?: string) => {
    try {
      const payload = reason ? { reason } : {};
      const response = await apiClient.post(`/admin/superadmin/admins/${uuid}/reject`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAdminEstablishments: async (uuid: string) => {
    try {
      const response = await apiClient.get(`/admin/superadmin/admins/${uuid}/establishments`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  assignEstablishment: async (adminUuid: string, establishmentUuid: string) => {
    try {
      const response = await apiClient.post(`/admin/superadmin/admins/${adminUuid}/establishments`, {
        establishment_uuid: establishmentUuid
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  revokeEstablishment: async (adminUuid: string, establishmentUuid: string) => {
    try {
      const response = await apiClient.delete(`/admin/superadmin/admins/${adminUuid}/establishments/${establishmentUuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const rewardsApi = {

  getAll: async (scope?: EstablishmentScopeFilters) => {
    try {
      const searchParams = new URLSearchParams();
      if (scope?.filter_assigned_admin_uuid)
        searchParams.set('filter_assigned_admin_uuid', scope.filter_assigned_admin_uuid);
      if (scope?.filter_project_owner_uuid)
        searchParams.set('filter_project_owner_uuid', scope.filter_project_owner_uuid);
      const q = searchParams.toString();
      const response = await apiClient.get(q ? `/rewards?${q}` : '/rewards');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getByLevel: async (level: string) => {
    try {
      const response = await apiClient.get(`/rewards/level/${level}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/rewards/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getByEstablishment: async (establishmentId: string, params?: { page?: number; page_size?: number }): Promise<Reward[] | { data: Reward[]; meta: ListMeta }> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page != null) searchParams.set('page', String(params.page));
      if (params?.page_size != null) searchParams.set('page_size', String(params.page_size));
      const query = searchParams.toString();
      const response = await apiClient.get(`/establishments/${establishmentId}/rewards${query ? `?${query}` : ''}`);
      return response.data as Reward[] | { data: Reward[]; meta: ListMeta };
    } catch (error) {
      throw error;
    }
  },

  getByEstablishmentAndLevel: async (establishmentId: string, level: string) => {
    try {
      const response = await apiClient.get(`/establishments/${establishmentId}/rewards/level/${level}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getForAllByEstablishment: async (establishmentId: string) => {
    try {
      const response = await apiClient.get(`/establishments/${establishmentId}/rewards/all`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (establishmentId: string, reward: Omit<CreateRewardRequest, 'establishment_uuid'>) => {
    try {
      const rewardData = {
        ...reward,
        establishment_uuid: establishmentId,

        valid_from: reward.valid_from ? new Date(reward.valid_from).toISOString() : null,
        valid_until: reward.valid_until ? new Date(reward.valid_until).toISOString() : null
      };
      const response = await apiClient.post('/rewards', rewardData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string, reward: UpdateRewardRequest) => {
    try {
      const rewardData = {
        ...reward,

        valid_from: reward.valid_from ? new Date(reward.valid_from).toISOString() : null,
        valid_until: reward.valid_until ? new Date(reward.valid_until).toISOString() : null
      };
      const response = await apiClient.put(`/rewards/${id}`, rewardData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/rewards/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserRewards: async () => {
    try {
      const response = await apiClient.get('/user/rewards');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserAvailableRewards: async () => {
    try {
      const response = await apiClient.get('/user/rewards/available');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  redeemReward: async (request: RedeemRewardRequest) => {
    try {
      const response = await apiClient.post('/user/rewards/redeem', request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  assignToUser: async (rewardId: string) => {
    try {
      const response = await apiClient.post('/user/rewards/assign', {
        reward_uuid: rewardId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export interface DashboardStats {
  total_revenue: number;
  total_orders: number;
  total_customers: number;
  total_items: number;
  revenue_today: number;
  orders_today: number;
  new_customers_today: number;
}

export interface EstablishmentScopeFilters {
  filter_assigned_admin_uuid?: string;
  filter_project_owner_uuid?: string;
}

export interface DashboardFilters {
  date_from?: string;
  date_to?: string;
  location_id?: string;
  filter_assigned_admin_uuid?: string;
  filter_project_owner_uuid?: string;
}

export interface DashboardEstablishmentItem {
  uuid: string;
  name: string;
  locations: { uuid: string; name: string }[];
}

export interface DashboardLocationItem {
  uuid: string;
  name: string;
  establishment_uuid: string;
  establishment_name: string;
  address?: string;
}

export interface DashboardEstablishmentsResponse {
  establishments_count: number;
  locations_count: number;
  establishments: DashboardEstablishmentItem[];
  locations: DashboardLocationItem[];
}

export interface ScopeFilterOptionAdmin {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  membership_role?: string;
}

export interface ScopeFilterOptionsResponse {
  project_members?: ScopeFilterOptionAdmin[];
  project_owners?: ScopeFilterOptionAdmin[];
}

export const projectScopeApi = {
  getFilterOptions: async (): Promise<ScopeFilterOptionsResponse> => {
    const response = await apiClient.get<ScopeFilterOptionsResponse>('/admin/project/scope-filter-options');
    return response.data;
  },
};

export const dashboardApi = {
  getStats: async (filters?: DashboardFilters): Promise<DashboardStats> => {
    const params = new URLSearchParams();
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.location_id) params.append('location_id', filters.location_id);
    if (filters?.filter_assigned_admin_uuid)
      params.append('filter_assigned_admin_uuid', filters.filter_assigned_admin_uuid);
    if (filters?.filter_project_owner_uuid)
      params.append('filter_project_owner_uuid', filters.filter_project_owner_uuid);
    const query = params.toString();
    const url = query ? `/admin/dashboard/stats?${query}` : '/admin/dashboard/stats';
    const response = await apiClient.get<DashboardStats>(url);
    return response.data;
  },

  getEstablishmentsAndLocations: async (
    scope?: EstablishmentScopeFilters,
  ): Promise<DashboardEstablishmentsResponse> => {
    const params = new URLSearchParams();
    if (scope?.filter_assigned_admin_uuid)
      params.append('filter_assigned_admin_uuid', scope.filter_assigned_admin_uuid);
    if (scope?.filter_project_owner_uuid)
      params.append('filter_project_owner_uuid', scope.filter_project_owner_uuid);
    const projectId = secureStorage.getActiveProjectId() ?? '';
    const query = params.toString();
    const cacheKey = `${projectId}::${query || 'default'}`;
    const url = query
      ? `/admin/dashboard/establishments-and-locations?${query}`
      : '/admin/dashboard/establishments-and-locations';

    return runInFlight(`dashboard:establishments-and-locations:${cacheKey}`, async () => {
      const response = await apiClient.get<DashboardEstablishmentsResponse>(url);
      return response.data;
    });
  },
};

export function clearDashboardApiCache(): void {}

export interface CustomerListItem {
  uuid: string;
  slug?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  status: string;
  created_at: string;
}

export interface CustomerListMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface CustomerListResponse {
  data: CustomerListItem[];
  meta: CustomerListMeta;
  error?: string;
}

export interface CustomerDetail {
  uuid: string;
  slug?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birth_date: string | null;
  gender: string;
  city: string;
  status: string;
  created_at: string;
  updated_at: string;
  card?: { points: number; level: string; card_number: string };
}

export interface CustomerAnalytics {
  total_orders: number;
  total_spent: number;
  loyalty_points: number;
  loyalty_level?: string;
  loyalty_source?: string;
  loyalty_establishment_uuid?: string;
  completed_orders: number;
  completion_rate: number;
  average_order: number;
  orders_by_status: Record<string, number>;
  orders_by_day: Array<{
    date: string;
    orders_count: number;
    spent: number;
  }>;
}

export interface UpdateCustomerRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  birth_date?: string | null;
  gender?: string;
  city?: string;
  status?: string;
}

export const customersApi = {
  getList: async (params?: { page?: number; page_size?: number; search?: string }): Promise<CustomerListResponse> => {
    const response = await apiClient.get<CustomerListResponse>('/admin/customers', { params });
    return response.data;
  },
  getById: async (id: string): Promise<CustomerDetail> => {
    const response = await apiClient.get<CustomerDetail>(`/admin/customers/${id}`);
    return response.data;
  },
  update: async (id: string, body: UpdateCustomerRequest): Promise<{ message: string }> => {
    const response = await apiClient.put<{ message: string }>(`/admin/customers/${id}`, body);
    return response.data;
  },
  getAnalytics: async (
    id: string,
    params?: { date_from?: string; date_to?: string; establishment_uuid?: string }
  ): Promise<CustomerAnalytics> => {
    const response = await apiClient.get<CustomerAnalytics>(`/admin/customers/${id}/analytics`, { params });
    return response.data;
  },
};

export interface PromoCodeListItem {
  uuid: string;
  establishment_uuid: string;
  establishment_name?: string;
  code: string;
  discount_type: string;
  discount_value: number;
  min_order_amount: number;
  max_uses: number;
  used_count: number;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  created_at: string;
}

export interface PromoCodeDetail extends PromoCodeListItem {
  updated_at: string;
}

export interface PromoCodeListMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface PromoCodeListResponse {
  data: PromoCodeListItem[];
  meta?: PromoCodeListMeta;
}

export interface CreatePromoCodeRequest {
  establishment_uuid: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_amount?: number;
  max_uses?: number;
  valid_from: string;
  valid_to: string;
  is_active?: boolean;
}

export interface UpdatePromoCodeRequest {
  code?: string;
  discount_type?: 'percentage' | 'fixed';
  discount_value?: number;
  min_order_amount?: number;
  max_uses?: number;
  valid_from?: string;
  valid_to?: string;
  is_active?: boolean;
}

export const promoCodeApi = {
  getList: async (
    params?: {
      establishment_id?: string;
      page?: number;
      page_size?: number;
      filter_assigned_admin_uuid?: string;
      filter_project_owner_uuid?: string;
    }
  ): Promise<PromoCodeListResponse> => {
    const response = await apiClient.get<PromoCodeListResponse>('/admin/promo-codes', { params });
    return response.data;
  },
  getById: async (id: string): Promise<PromoCodeDetail> => {
    const response = await apiClient.get<PromoCodeDetail>(`/admin/promo-codes/${id}`);
    return response.data;
  },
  create: async (body: CreatePromoCodeRequest): Promise<{ uuid: string; message: string }> => {
    const response = await apiClient.post<{ uuid: string; message: string }>('/admin/promo-codes', body);
    return response.data;
  },
  update: async (id: string, body: UpdatePromoCodeRequest): Promise<{ message: string }> => {
    const response = await apiClient.put<{ message: string }>(`/admin/promo-codes/${id}`, body);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/admin/promo-codes/${id}`);
    return response.data;
  },
};

export const tableApi = {

  getTablesForLocation: async (locationId: string) => {
    try {
      const response = await apiClient.get(`/locations/${locationId}/tables`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTable: async (tableId: string) => {
    try {
      const response = await apiClient.get(`/tables/${tableId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createTable: async (locationId: string, table: TableRequest) => {
    try {
      const response = await apiClient.post(`/locations/${locationId}/tables`, table);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  batchCreateTables: async (locationId: string, batchRequest: TableBatchRequest) => {
    try {
      const response = await apiClient.post(`/locations/${locationId}/tables/batch`, batchRequest);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateTable: async (tableId: string, table: TableRequest) => {
    try {
      const response = await apiClient.put(`/tables/${tableId}`, table);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteTable: async (tableId: string) => {
    try {
      const response = await apiClient.delete(`/tables/${tableId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  generateQRCode: async (tableId: string) => {
    try {
      const response = await apiClient.get(`/tables/${tableId}/qrcode`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  generateBatchQRCodes: async (locationId: string, request: QRBatchGenerateRequest) => {
    try {
      const response = await apiClient.post(`/locations/${locationId}/tables/qrcodes`, request);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const officeAdminApi = {

  getAll: async () => {
    try {
      const response = await apiClient.get('/office-admins');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/office-admins/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  invite: async (payload: { name: string; email: string; location_id: string }) => {
    const response = await apiClient.post('/office-admins/invite', payload);
    return response.data;
  },

  resendInvite: async (id: string) => {
    const response = await apiClient.post(`/office-admins/${id}/resend-invite`);
    return response.data;
  },

  create: async (officeAdmin: any) => {
    try {
      const response = await apiClient.post('/office-admins', officeAdmin);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string, officeAdmin: any) => {
    try {

      const response = await apiClient.put(`/office-admins/${id}`, officeAdmin);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/office-admins/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setStatus: async (id: string, active: boolean) => {
    try {
      const response = await apiClient.patch(`/office-admins/${id}/status?active=${active}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export interface POSProvider {
  code: string;
  name: string;
  description: string;
}

export interface POSCredentials {
  [key: string]: string;
}

export interface POSCredentialsPreview {
  values?: POSCredentials;
  configured?: Record<string, boolean>;
}

export interface POSIntegration {
  uuid: string;
  location_id: string;
  provider_code: string;
  provider_name?: string;
  is_active: boolean;
  ordering_enabled?: boolean;
  order_hub_base_url?: string;
  pos_store_id?: string;
  credentials?: POSCredentials;
  credentials_preview?: POSCredentialsPreview;

  external_orders_api_key?: string;
  last_sync_at?: string;
  last_sync_err?: string;
  created_at: string;
  updated_at: string;
}

export interface POSIntegrationCreateRequest {
  provider_code: string;
  credentials: POSCredentials;
}

export interface POSIntegrationUpdateRequest {
  credentials?: POSCredentials;
  is_active?: boolean;
  ordering_enabled?: boolean;
}

export interface POSMenuMapping {
  uuid: string;
  integration_id: string;
  menu_item_id: string;
  menu_item_name?: string;
  pos_article_id: string;
  pos_article_code?: string;
  pos_category_id?: string;
  /** False hides the item in the app at this location. Missing means available. */
  available?: boolean;
  created_at: string;
  updated_at: string;
}

export interface POSMenuMappingUpsertRequest {
  menu_item_id: string;
  pos_article_id: string;
  pos_article_code?: string;
  pos_category_id?: string;
  /** False hides the item in the app at this location. Omit to leave the current visibility. */
  available?: boolean;
}

export const posApi = {

  getProviders: async (): Promise<POSProvider[]> => {
    const response = await apiClient.get('/pos/providers');
    return response.data;
  },

  getIntegration: async (locationId: string): Promise<POSIntegration | null> => {
    if (!locationId) {
      return null;
    }
    try {
      const response = await apiClient.get(`/locations/${locationId}/pos`);
      const data = response.data;
      if (!data || typeof data !== 'object' || !('uuid' in data) || !(data as POSIntegration).uuid) {
        return null;
      }
      return data as POSIntegration;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw err;
    }
  },

  createIntegration: async (locationId: string, data: POSIntegrationCreateRequest): Promise<POSIntegration> => {
    const response = await apiClient.post(`/locations/${locationId}/pos`, data);
    return response.data;
  },

  updateIntegration: async (locationId: string, data: POSIntegrationUpdateRequest): Promise<POSIntegration> => {
    const response = await apiClient.put(`/locations/${locationId}/pos`, data);
    return response.data;
  },

  deleteIntegration: async (locationId: string): Promise<void> => {
    await apiClient.delete(`/locations/${locationId}/pos`);
  },

  syncMenu: async (locationId: string): Promise<{ message: string; count: number; items: unknown[] }> => {
    const response = await apiClient.post(`/locations/${locationId}/pos/sync-menu`);
    return response.data;
  },

  testRemarisConnection: async (locationId: string): Promise<{ ok: boolean; message: string }> => {
    const response = await apiClient.post(`/locations/${locationId}/pos/test-connection`);
    return response.data;
  },

  regenerateExternalApiKey: async (locationId: string): Promise<POSIntegration> => {
    const response = await apiClient.post(`/locations/${locationId}/pos/regenerate-external-api-key`);
    return response.data;
  },

  listMenuMappings: async (locationId: string): Promise<POSMenuMapping[]> => {
    const response = await apiClient.get(`/locations/${locationId}/pos/menu-mappings`);
    return response.data ?? [];
  },

  upsertMenuMapping: async (
    locationId: string,
    data: POSMenuMappingUpsertRequest,
  ): Promise<POSMenuMapping> => {
    const response = await apiClient.post(`/locations/${locationId}/pos/menu-mappings`, data);
    return response.data;
  },

  deleteMenuMapping: async (locationId: string, mappingId: string): Promise<void> => {
    await apiClient.delete(`/locations/${locationId}/pos/menu-mappings/${mappingId}`);
  },

  /**
   * Shows or hides a mapped item in the app at this location.
   * @param locationId Location that owns the POS integration.
   * @param mappingId Mapping row to update.
   * @param available False hides the item at this location only.
   * @returns Updated mapping.
   */
  setMenuMappingAvailability: async (
    locationId: string,
    mappingId: string,
    available: boolean,
  ): Promise<POSMenuMapping> => {
    const response = await apiClient.put(
      `/locations/${locationId}/pos/menu-mappings/${mappingId}/availability`,
      { available },
    );
    return response.data;
  },
};

export interface PaymentProvider {
  code: string;
  name: string;
  description: string;
}

export interface PaymentCredentials {
  [key: string]: string;
}

export interface PaymentSettings {
  currency?: string;
  test_mode?: boolean;
  cash_on_site_enabled?: boolean;
  enabled_methods?: string[];
}

export interface PaymentIntegration {
  uuid: string;
  establishment_uuid: string;
  provider_code: string;
  provider_name?: string;
  is_active: boolean;
  settings: PaymentSettings;
  webhook_base_url?: string;
  webhook_secret?: string;
  credential_fields_set?: string[];
  created_at: string;
  updated_at: string;
}

export interface PaymentIntegrationCreateRequest {
  provider_code: string;
  credentials: PaymentCredentials;
  settings?: PaymentSettings;
}

export interface PaymentIntegrationUpdateRequest {
  credentials?: PaymentCredentials;
  settings?: PaymentSettings;
  is_active?: boolean;
}

export const paymentApi = {
  getProviders: (() => {
    let cached: Promise<PaymentProvider[]> | null = null;
    return async (): Promise<PaymentProvider[]> => {
      if (!cached) {
        cached = apiClient
          .get('/payments/providers')
          .then((response) => response.data as PaymentProvider[])
          .catch((err) => {
            cached = null;
            throw err;
          });
      }
      return cached;
    };
  })(),

  getIntegration: async (establishmentId: string): Promise<PaymentIntegration | null> => {
    return runInFlight(`payments:${establishmentId}`, async () => {
      const response = await apiClient.get(`/establishments/${establishmentId}/payments`);
      return response.data;
    });
  },

  createIntegration: async (
    establishmentId: string,
    data: PaymentIntegrationCreateRequest,
  ): Promise<PaymentIntegration> => {
    const response = await apiClient.post(`/establishments/${establishmentId}/payments`, data);
    return response.data;
  },

  updateIntegration: async (
    establishmentId: string,
    data: PaymentIntegrationUpdateRequest,
  ): Promise<PaymentIntegration> => {
    const response = await apiClient.put(`/establishments/${establishmentId}/payments`, data);
    return response.data;
  },

  deleteIntegration: async (establishmentId: string): Promise<void> => {
    await apiClient.delete(`/establishments/${establishmentId}/payments`);
  },

  testConnection: async (establishmentId: string): Promise<{ ok: boolean; message: string }> => {
    const response = await apiClient.post(`/establishments/${establishmentId}/payments/test-connection`);
    return response.data;
  },

  getProjectContext: async (establishmentId: string): Promise<EstablishmentPaymentContext> => {
    const response = await apiClient.get(`/establishments/${establishmentId}/payments/project-context`);
    return response.data as EstablishmentPaymentContext;
  },

  getProjectPaymentSettings: async (projectUuid: string): Promise<ProjectPaymentSettings> => {
    const response = await apiClient.get(`/admin/projects/${projectUuid}/payment-settings`);
    return response.data as ProjectPaymentSettings;
  },

  updateProjectPaymentProvider: async (
    projectUuid: string,
    providerCode: string,
  ): Promise<ProjectPaymentSettings> => {
    const response = await apiClient.put(`/admin/projects/${projectUuid}/payment-settings`, {
      provider_code: providerCode,
    });
    return response.data as ProjectPaymentSettings;
  },

  connectProjectStripe: async (
    projectUuid: string,
    data: StripeProjectConnectRequest,
  ): Promise<ProjectPaymentSettings> => {
    const response = await apiClient.post(
      `/admin/projects/${projectUuid}/payment-settings/stripe/connect`,
      data,
    );
    return response.data as ProjectPaymentSettings;
  },

  getStripeConnectStatus: async (establishmentId: string): Promise<StripeConnectStatus | null> => {
    try {
      const response = await apiClient.get(`/establishments/${establishmentId}/stripe-connect/status`);
      return response.data as StripeConnectStatus;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  refreshStripeConnectStatus: async (establishmentId: string): Promise<StripeConnectStatus | null> => {
    try {
      const response = await apiClient.post(`/establishments/${establishmentId}/stripe-connect/refresh`);
      return response.data as StripeConnectStatus;
    } catch (err) {
      if (axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 409)) {
        return null;
      }
      throw err;
    }
  },

  createStripeConnectAccount: async (establishmentId: string): Promise<StripeConnectAccountCreated> => {
    const response = await apiClient.post(`/establishments/${establishmentId}/stripe-connect/account`);
    return response.data as StripeConnectAccountCreated;
  },

  createStripeConnectAccountSession: async (
    establishmentId: string,
  ): Promise<StripeConnectAccountSession> => {
    const response = await apiClient.post(`/establishments/${establishmentId}/stripe-connect/account-session`);
    return response.data as StripeConnectAccountSession;
  },

  createStripeConnectAccountLink: async (
    establishmentId: string,
  ): Promise<StripeConnectAccountLink> => {
    const response = await apiClient.post(`/establishments/${establishmentId}/stripe-connect/account-link`);
    return response.data as StripeConnectAccountLink;
  },
};

export const orderingApi = {
  getSettings: async (establishmentId: string): Promise<import('@/types/ordering').OrderingSettings> => {
    return runInFlight(`ordering:${establishmentId}`, async () => {
      const response = await apiClient.get(`/establishments/${establishmentId}/ordering`);
      return response.data;
    });
  },

  updateSettings: async (
    establishmentId: string,
    data: import('@/types/ordering').OrderingSettingsUpdateRequest,
  ): Promise<import('@/types/ordering').OrderingSettings> => {
    const response = await apiClient.put(`/establishments/${establishmentId}/ordering`, data);
    return response.data;
  },
};

export interface BroadcastRequest {
  title: string;
  body: string;
  establishment_id?: string;
  location_id?: string;
}

export interface BroadcastResponse {
  message: string;
  total_users: number;
  total_devices: number;
  success_sent: number;
  failed_sent: number;
}

export const ordersApi = {
  list: async (params: { location_id?: string; status?: string }): Promise<OrderListItem[]> => {
    const searchParams = new URLSearchParams();
    if (params.location_id) searchParams.set('location_id', params.location_id);
    if (params.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    const response = await apiClient.get(`/orders${query ? `?${query}` : ''}`);
    return extractApiList<OrderListItem>(response.data);
  }
};

export const notificationApi = {

  sendBroadcast: async (data: BroadcastRequest): Promise<BroadcastResponse> => {
    const response = await apiClient.post('/admin/notifications/broadcast', data);
    return response.data;
  }
};

export interface ManualPurchaseRequest {
  user_identifier: string;
  amount: number;
  location_id: string;
  description?: string;
}

export interface ManualPurchaseResponse {
  success: boolean;
  message: string;
  user_name: string;
  added_points: number;
  total_points: number;
  level: string;
}

export const purchaseApi = {

  manualPurchase: async (data: ManualPurchaseRequest): Promise<ManualPurchaseResponse> => {
    const response = await apiClient.post('/admin/purchases/manual', data);
    return response.data;
  }
};

export default {
  establishmentApi,
  locationApi,
  menuApi,
  newsApi,
  mediaApi,
  authApi,
  adminApi,
  rewardsApi,
  dashboardApi,
  tableApi,
  officeAdminApi,
  posApi,
  paymentApi,
  orderingApi,
  notificationApi,
  purchaseApi
};